import React, { useState } from "react";
import { Typography, Box, TextField } from "@mui/material";
import {
    ShepDataGridPro,
    ShepPagination,
    ShepCheckbox,
    ShepTooltip,
    ShepGridWarning,
    ShepHorizontalMenu,
} from "@/components/GenericComponents/ShepComponents";
import CustomFilterDropdown from "@/components/CustomFilterDropdown/CustomFilterDropdown";

const ShepTable = ({
    tableTitle, // Leave undefined to disable title header
    columnTitles,
    isDataLoaded = true,
    dataRows,
    schemas,
    selectedRow,
    searchValue,
    handleSearchChange,
    handleSelectedRowChange,
    getRowId,
    tableActions,
    checkboxesEnabled = false,
    enableRowClickSelectionWithCheckboxes = false,
    selectionDisabled = false,
    emptyMessage,
    noSearchResultsMessage = {
        title: "No Search Results",
    },
    initialState,
    columnVisibilityModel,
    apiRef,
}) => {
    // Picks the correct noResultMessage to display. searchValue required to work properly.
    const noResultMessage =
        (searchValue ?? "").trim() === "" ? emptyMessage : noSearchResultsMessage;

    return (
        <Box sx={{ display: "block", flexDirection: "column" }}>
            <Box style={{ display: "flex", paddingBottom: 5 }}>
                {schemas && <CustomFilterDropdown schemas={schemas} />}
                {handleSearchChange && (
                    <TextField
                        placeholder={"Search"}
                        value={searchValue}
                        onChange={handleSearchChange}
                        sx={{
                            paddingLeft: schemas ? "10px" : "0",
                            width: "450px",
                            "&.MuiFormControl-root .MuiInputBase-root": {
                                borderRadius: "5px",
                            },
                        }}
                    />
                )}
                {tableActions && (
                    <div
                        style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "flex-end",
                            paddingBottom: "8px",
                        }}
                    >
                        <ShepHorizontalMenu actions={tableActions} />
                    </div>
                )}
            </Box>
            {tableTitle && (
                <Box
                    sx={{
                        height: "78px",
                        backgroundColor: "var(--primary-main)",
                        color: "var(--default-light)",
                        padding: "20px 0px 0px 53px",
                        borderRadius: "4px",
                        border: "1px black",
                        boxSizing: "border-box",
                    }}
                >
                    <Typography
                        style={{
                            fontSize: "var(--font-lg)",
                            fontWeight: "var(--font-semi-bold)",
                        }}
                    >
                        {tableTitle}
                    </Typography>
                </Box>
            )}
            {dataRows && dataRows.length > 0 ? (
                <ShepDataGridPro
                    apiRef={apiRef}
                    isDataLoaded={isDataLoaded}
                    secondsToStall={20}
                    rows={dataRows}
                    columns={columnTitles}
                    getRowId={getRowId}
                    columnHeaderHeight={80}
                    rowHeight={80}
                    pagination
                    initialState={{
                        pagination: {
                            paginationModel: { pageSize: 15, page: 0 },
                        },
                        ...initialState,
                    }}
                    whiteHeader={true}
                    checkboxSelection={checkboxesEnabled && !selectionDisabled}
                    columnVisibilityModel={columnVisibilityModel}
                    disableMultipleRowSelection={false}
                    disableColumnMenu={true}
                    disableColumnFilter={true}
                    disableColumnReorder={true}
                    hideFooterSelectedRowCount={true}
                    disableRowSelectionOnClick={
                        (checkboxesEnabled && !enableRowClickSelectionWithCheckboxes) ||
                        selectionDisabled
                    }
                    onRowSelectionModelChange={handleSelectedRowChange} // Capture row selection
                    rowSelectionModel={selectedRow}
                    slots={{
                        baseCheckbox: ShepCheckbox,
                        baseTooltip: ShepTooltip,
                        pagination: ShepPagination,
                    }}
                    sx={{
                        color: "var(--text-primary)",
                        fontSize: "var(--font-sm)",
                        fontFamily: "var(--font-family)",
                        borderTopLeftRadius: "12px",
                        borderTopRightRadius: "12px",
                        backgroundColor: "var(--default-light)",
                        border: "none",
                        "& .MuiDataGrid:hover": {
                            backgroundColor: "inherit", // Or 'transparent' or whatever color you'd like
                        },
                        "& .MuiDataGrid-cellCheckbox, & .workflow-row": {
                            opacity: 0,
                        },
                        "& .MuiDataGrid-cell": {
                            borderBottom: "none",
                        },
                        "& .MuiDataGrid-row": {
                            overflow: "hidden",
                        },
                        "& .MuiDataGrid-row:hover": {
                            backgroundColor: "var(--tertiary-13)",
                        },
                        "& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover":
                        {
                            borderTopLeftRadius: "4px",
                            borderBottomLeftRadius: "4px",
                            backgroundColor: "var(--tertiary-14)",
                        },
                        "& .MuiDataGrid-row.Mui-selected::before": {
                            // This is the little ribbon
                            content: '""',
                            outline: "8px solid var(--primary-main)",
                            height: "inherit",
                        },
                        "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus":
                        {
                            outline: "none",
                        },
                        "& .MuiDataGrid-row:hover .MuiDataGrid-cellCheckbox, & .MuiDataGrid-row:hover .workflow-row":
                        {
                            opacity: 1,
                        },
                        "& .Mui-selected .MuiDataGrid-cellCheckbox": {
                            opacity: 1,
                        },
                        "& .MuiDataGrid-columnHeaderTitle": {
                            fontSize: "var(--font-md)",
                            fontWeight: "var(--font-bold)",
                            borderLeft: "none",
                        },
                        "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader:hover": {
                            borderBottomLeftRadius: "4px",
                            borderBottomRightRadius: "4px",
                            backgroundColor: "var(--primary-pale)",
                            borderBottom: "none",
                            borderLeft: "none",
                        },
                        "& .MuiDataGrid-columnHeader[data-field='displayName']": {
                            marginLeft: 0,
                        },
                        "& .MuiDataGrid-cell[data-field='displayName']": {
                            marginLeft: 0,
                        },
                        "& .MuiDataGrid-row > div:first-of-type, & .MuiDataGrid-columnHeadersInner > * > div:first-of-type":
                        {
                            paddingLeft: checkboxesEnabled ? 0 : "60px", // Replace checkbox with padding
                        },
                    }}
                />
            ) : (
                <ShepGridWarning
                    title={noResultMessage?.title}
                    message={noResultMessage?.message}
                />
            )}
        </Box>
    );
};

export default ShepTable;
